// ./src/components/features/AnalyticsConsent/AnalyticsConsentBanner.tsx
//
// THE ONE-TIME ANALYTICS QUESTION (ROADMAP #62 P7, owner 2026-10-01: "Europe asks, rest on"). Shown once per browser,
// and only to a browser on a European clock (`needsConsentPrompt`); the answer is remembered on this browser and can be
// changed later on the profile page (`AnalyticsConsentCard`), which this copy promises — so that card must exist.
//
// ⓘ Mounted ONCE at the app root, outside the routes, so it reaches the sign-up page too (where `sign_up` is counted).
// ⓘ Allow and Decline are the same size, side by side: declining must be as easy as agreeing.
// ⚠ A crew member with no account (`/crew-access`, then a crew session) has NO profile page, so the "change it later"
//   sentence is not shown to them — it would be a promise nothing keeps. (Clearing the site's data asks again.)
import React from 'react';
import { Affix, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useBranding } from '@/contexts/BrandingContext';
import { AuthService } from '@/utils/api/AuthService';
import { CrewSessionService } from '@/utils/api/CrewSessionService';

import { useAnalyticsConsent } from './useAnalyticsConsent';

export const AnalyticsConsentBanner: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { brand } = useBranding();
  const { needsPrompt, setConsent } = useAnalyticsConsent();

  if (!needsPrompt) {
    return null;
  }

  const crewWithoutAccount =
    window.location.pathname.startsWith('/crew-access') || (CrewSessionService.hasSession() && !AuthService.getStoredUserData());

  return (
    <Affix position={{ bottom: 16, left: 16, right: 16 }} zIndex={1000}>
      <Paper
        withBorder
        shadow="lg"
        radius="md"
        p="md"
        maw={560}
        mx="auto"
        role="region"
        aria-label={t('common:analyticsConsent.label', { defaultValue: 'Analytics cookies' })}
      >
        <Stack gap="sm">
          <Text size="md">
            {t('common:analyticsConsent.message', {
              defaultValue:
                '{{brand}} would like to use Google Analytics cookies to learn how the app is used and make it better, and may use them to run and measure its own ads.',
              brand: brand.name,
            })}
            {crewWithoutAccount
              ? null
              : ` ${t('common:analyticsConsent.changeLater', { defaultValue: 'You can change this later on your profile page.' })}`}
          </Text>
          <Group justify="flex-end" gap="sm" grow>
            <Button variant="default" size="md" onClick={() => setConsent('denied')}>
              {t('common:analyticsConsent.decline', { defaultValue: 'Decline' })}
            </Button>
            <Button variant="default" size="md" onClick={() => setConsent('granted')}>
              {t('common:analyticsConsent.allow', { defaultValue: 'Allow' })}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Affix>
  );
};
